import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff } from 'lucide-react';

export default function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword.trim()) {
      toast.error('Password lama wajib diisi');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password baru minimal 6 karakter');
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('Password baru harus berbeda dari password lama');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Konfirmasi password tidak cocok');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.put('/users/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success(res.data?.message || 'Password berhasil diganti');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal ganti password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl space-y-8">
      <div className="pb-2 border-b border-slate-200/70">
        <h1 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 tracking-tight" data-testid="change-password-page-title">
          Ganti Password
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Perbarui password akun Anda</p>
      </div>

      <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-password" className="text-sm font-medium text-slate-700">
              Password Lama
            </Label>
            <div className="relative">
              <Input
                id="current-password"
                data-testid="current-password-input"
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                placeholder="Password lama"
                className="w-full border-slate-200 rounded-xl text-sm pr-10 placeholder:text-slate-300 focus-visible:ring-[#1A3C34]"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-password" className="text-sm font-medium text-slate-700">
              Password Baru (min. 6 karakter)
            </Label>
            <div className="relative">
              <Input
                id="new-password"
                data-testid="new-password-input"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Password baru"
                className="w-full border-slate-200 rounded-xl text-sm pr-10 placeholder:text-slate-300 focus-visible:ring-[#1A3C34]"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password" className="text-sm font-medium text-slate-700">
              Konfirmasi Password Baru
            </Label>
            <div className="relative">
              <Input
                id="confirm-password"
                data-testid="confirm-password-input"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Ulangi password baru"
                className="w-full border-slate-200 rounded-xl text-sm pr-10 placeholder:text-slate-300 focus-visible:ring-[#1A3C34]"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="change-password-submit"
            className="w-full bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md disabled:opacity-60"
          >
            {submitting ? (
              'Menyimpan...'
            ) : (
              <>
                <Lock className="w-4 h-4 mr-2" />
                Simpan Password
              </>
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
}